import {Component, OnChanges, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {NgOptimizedImage} from '@angular/common';
import {ClickService} from '../../../../services/click/click.service';
import {LoaderService} from '../../../../services/loader/loader.service';
import {Paginator} from '../../../../tools/pagination/paginator';
import {TableSorter} from '../../../../tools/table-sorter/table-sorter';
import {Click} from '../../../../models/click';
import {PaginatorComponent} from '../../../layout/paginator/paginator.component';
import {ParisDatePipe} from '../../../../pipes/date/paris-date.pipe';
import {TableSorterHeaderDirective} from '../../../../directives/table-sorter/table-sorter-header.directive';
import {TableSorterDirective} from '../../../../directives/table-sorter/table-sorter.directive';
import {FormsModule} from '@angular/forms';
import {PartnerService} from '../../../../services/partner/partner.service';
import {Partner} from '../../../../models/partner/partner';
import {IsGranted} from '../../../../decorator/is-granted';
import {MetaTitle} from '../../../../decorator/meta-title';
import {QueryFilterService} from '../../../../services/query-filter/query-filter.service';
import {ClickDetailComponent} from './click-detail/click-detail.component';
import {DialogService} from '../../../../services/dialog/dialog.service';
import {CopyToClipboardComponent} from '../../../layout/copy-to-clipboard/copy-to-clipboard.component';
import {LogForm} from '../../../../forms/log-form';
import {LogFilter} from '../../../../types/log-filter';
import {LogFiltersComponent} from '../../../layout/log-filters/log-filters.component';
import {DisplayTableColumnComponent} from '../../../layout/display-table-column/display-table-column.component';
import {getTableHeaders} from '../../../../helpers/table.helper';
import {CsvExportService} from '../../../../services/csv-export/csv-export.service';
import {SnackbarService} from '../../../../services/snackbar/snackbar.service';
import {CsvExportProgressComponent} from '../../../layout/csv-export-progress/csv-export-progress.component';
import {CountryService} from '../../../../services/country/country.service';

@Component({
  selector: 'app-click-log',
  imports: [
    PaginatorComponent,
    ParisDatePipe,
    TableSorterHeaderDirective,
    TableSorterDirective,
    FormsModule,
    CopyToClipboardComponent,
    LogFiltersComponent,
    DisplayTableColumnComponent,
    CsvExportProgressComponent,
    NgOptimizedImage,
  ],
  templateUrl: './click-log.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './click-log.component.scss'
})
@IsGranted(['CLICK_VIEW'])
@MetaTitle('Click logs')
export class ClickLogComponent implements OnInit, OnChanges {

  partners: Partner[] = [];
  clicks: Click[] = [];
  paginator: Paginator = new Paginator();
  tableSorter: TableSorter = new TableSorter('createdAt', 'desc');

  displayedColumns = {
    product: true,
    offer: true,
    conversionType: false,
    accountType: false,
    accountManager: false,
    landingPage: true,
    partner: true,
    country: true,
    subid: false,
    tc1: false,
    tc2: false,
    tc3: false,
    tc4: false,
    clickid: false,
    gclid: false,
    gbraid: false,
    wbraid: false,
    fbclid: false,
    fbpxid: false,
    msclkid: false,
    mspxid: false,
    twclid: false,
    twpxid: false,
    ttclid: false,
    ttpxid: false,
    source: false,
    ip: false,
    referer: false,
    userAgent: false,
    bot: false
  };

  filters: LogFilter = LogForm.initFilters();
  downloadUrl!: string;
  downloading: boolean = false;
  exportProgress: { current: number; total: number } | null = null;

  constructor(
    private loader: LoaderService,
    private partnerService: PartnerService,
    private clickService: ClickService,
    private queryFilterService: QueryFilterService,
    private dialog: DialogService,
    private csvExportService: CsvExportService,
    private snackbar: SnackbarService,
    protected countryService: CountryService,
  ) {
  }

  ngOnInit(): void {
    this.queryFilterService.init(this.paginator, this.tableSorter);

    this.filters = this.queryFilterService.initFilters();
    this.filters['bot'] = this.queryFilterService.getFilter('bot') ?? 'no-bot';

    this.rebuildUrl();

    this.loader.startLoading();
  }

  ngOnChanges(): void {
    this.rebuildUrl();
  }

  applyFilters(): void {
    this.paginator.currentPage = 1;
    this.fetchAll();
  }

  fetchAll(): void {
    this.loader.startLoading();

    const parameters = this.getParameters();
    this.queryFilterService.setFilters(parameters);

    this.clickService.findAll(parameters, this.paginator, this.tableSorter).subscribe(clicks => {
      this.clicks = clicks;
      this.loader.stopLoading();
    });
  }

  openDetail(click: Click): void {
    this.dialog.openDialog(ClickDetailComponent, {
      class: 'modal-lg',
      data: {
        id: click.id
      }
    }).subscribe(() => {
    });
  }

  downloadCsv(): void {
    if (this.downloading) {
      return;
    }

    const params = this.getParameters();
    params['headers'] = getTableHeaders('clicks');

    if (!params.startDate || !params.endDate) {
      this.snackbar.error('Please select a date range before exporting.');
      return;
    }

    this.downloading = true;
    this.exportProgress = null;

    this.csvExportService.export({
      endpoint: '/click/download',
      params,
      filename: 'click-logs.csv'
    }).subscribe({
      next: (event) => {
        if (event.type === 'progress') {
          this.exportProgress = { current: event.current, total: event.total };
        } else {
          this.downloading = false;
          this.exportProgress = null;
          if (event.truncatedChunks > 0) {
            this.snackbar.warning(
              `${event.truncatedChunks} of ${event.chunkCount} chunk(s) were truncated. The export may be incomplete — try a smaller date range.`
            );
          }
        }
      },
      error: () => {
        this.downloading = false;
        this.exportProgress = null;
        this.snackbar.error('CSV export failed. Please try again.');
      }
    });
  }

  private getParameters(): any {
    let params = LogForm.getFilters(this.filters, 'landingPage,product,offer,partner,accountManager');

    // Always sent so that "All traffic" (empty value) survives a reload from the URL
    params.bot = this.filters['bot'] ?? '';

    return params;
  }

  private rebuildUrl(): void {
    const parameters: any = this.getParameters();
    parameters['headers'] = getTableHeaders('clicks');
    this.downloadUrl = this.clickService.getDownloadUrl(parameters);
  }
}
